import * as ActionHandler from '../../ActionHandler';

import { AuthProvider, CredentialProps } from './Types';
import { NativeEventEmitter, NativeModules, Platform } from 'react-native';
import {
  dispatchAction,
  getRootAppKey,
  setRefreshToken,
} from '../../utilities/NativeHelpers';

import AsyncStorage from '@react-native-async-storage/async-storage';
import Environment from '../../Environment';
import GetAppHeaders from '../../Downloader/GetAppHeaders';
import appendUrlParams from '../../utilities/appendUrlParams';

const debug = require('debug')('omni:kit:AuthService');

const { ReactPlatformBridge } = NativeModules;

interface ListenerObject {
  context?: string; // useful for debugging which contexts auth came from
  callback: (data: { token: string | null }) => void;
}

export default class AuthService {
  private static _accessToken: string | null = null;
  private static _refreshToken: string | null = null;
  private static _refreshTokenEndpoint: string | null = null;

  public static async loadCredentials(
    props?: CredentialProps
  ): Promise<string | null> {
    /**
     * Restore session from cached credentials:
     * These credentials are less likely to be expired compared to root props
     * and have a higher chance of producing cache-hits for feed requests
     * that require Authorization since the accessToken is refreshed
     * within this method prior to rendering children of the provider.
     */
    await this.loadSavedCredentials();

    /**
     * Check root props for credentials, but only apply if unset in AuthService
     * to avoid cache-misses due to Authorization mismatch on protected content
     * (e.g. User Profile Block Page screen in offline state)
     */
    if (props?.accessToken && !AuthService.accessToken) {
      this.accessToken = props?.accessToken;
    }

    if (props?.sapToken && !AuthService.refreshToken) {
      this.refreshToken = props?.sapToken;
    }

    /**
     * User may have switched apps in a container app.
     * Always use tokenUrl from props when available
     * to avoid making requests with wrong app_key param in /token URL
     */
    if (props?.tokenUrl) {
      this.refreshTokenEndpoint = props?.tokenUrl;
    }

    /**
     * The access token may be expired.
     * Try to keep the user logged-in by executing token refresh.
     * If the refreshToken is expired, then token refresh will fail
     * and the user must be prompted to login.
     *
     * We ignore errors such as network request failure when user is offline
     * and keep the token state as-is so that we can continue to get cache-hits
     * on feed request that rely on Authorization such as Block Page User Profile
     *
     * However, if a 401/403 occurs, the token state will be cleared
     * and user will be prompted to login.
     */
    if (
      this.refreshTokenEndpoint &&
      (this.refreshToken ||
        Platform.OS === 'web') /* cookie-based refresh on web */
    ) {
      try {
        await this.refreshAccessToken(props?.source);
      } catch {}
    }

    return this.accessToken;
  }

  public static get refreshToken(): string | null {
    return this._refreshToken;
  }

  public static set refreshToken(value: string | null) {
    if (value) {
      AsyncStorage.setItem('refresh_token', value);
    } else {
      AsyncStorage.removeItem('refresh_token');
    }

    this._refreshToken = value;
  }

  public static sendRefreshTokenToNative(): void {
    if (this.refreshToken) {
      setRefreshToken(this.refreshToken);
    }
  }

  public static get refreshTokenEndpoint(): string | null {
    return this._refreshTokenEndpoint;
  }

  public static set refreshTokenEndpoint(value: string | null) {
    if (value) {
      AsyncStorage.setItem('refresh_token_endpoint', value);
    } else {
      AsyncStorage.removeItem('refresh_token_endpoint');
    }

    this._refreshTokenEndpoint = value;
  }

  public static get accessToken(): string | null {
    return this._accessToken;
  }

  public static set accessToken(token: string | null) {
    this._accessToken = token;
    if (token) {
      AsyncStorage.setItem('access_token', token);
    } else {
      AsyncStorage.removeItem('access_token');
    }

    this.notifyAuthorizationChanged();
  }

  private static notifyAuthorizationChanged() {
    const token = this._accessToken;
    this._dispatch('AuthorizationChanged', { token });
  }

  public static initialize(): () => void {
    if (ReactPlatformBridge) {
      const _listener = (event: { token?: string; refreshToken?: string }) => {
        this.refreshToken = event.refreshToken || null;
        this.accessToken = event.token || null;
      };

      const ReactPlatformBridgeEventEmitter = new NativeEventEmitter(
        ReactPlatformBridge
      );
      const bridgeListener = ReactPlatformBridgeEventEmitter.addListener(
        'AuthorizationChanged',
        _listener
      );

      return () => {
        bridgeListener.remove();
      };
    }

    return () => null;
  }

  public static async loadSavedCredentials(): Promise<string | null> {
    const refreshToken = await AsyncStorage.getItem('refresh_token');

    if (Boolean(refreshToken)) {
      this.refreshToken = refreshToken;
    }

    const endpoint = await AsyncStorage.getItem('refresh_token_endpoint');

    if (Boolean(endpoint)) {
      this.refreshTokenEndpoint = endpoint;
    }

    const token = await AsyncStorage.getItem('access_token');

    // avoid triggering an auth change event if no token is set
    if (Boolean(token)) {
      this.accessToken = token;
    }

    return token;
  }

  /**
   * Allows logging in with a given auth provider through the native bridge. Only works in the native
   * apps.
   * ref: https://subsplash.atlassian.net/wiki/spaces/BKND/pages/208797699/End+User+Login+Workflow#Target-Provider
   * @param targetProvider - The AuthProvider which will issue the Bearer token (usually Subsplash)
   * @param selectedProvider - The selected AuthProvider which the user logs in with
   */
  public static async loginFromNativeBridge(
    targetProvider: AuthProvider,
    selectedProvider: AuthProvider,
    source?: string
  ): Promise<void> {
    /**
     * PLAT-1509: Handle special case where we must force web-based Google OAuth
     * to use external browser on Kindle.
     *
     * MED-5393: The request will not have the standard 'sap-*' request headers,
     * so we need to append a root_app_key query param so that the user will be
     * redirected back to TCA (GH936H) as expected on Kindle.
     *
     * TODO: This android workaround is being done here so it can be delivered
     * through Code Push to Kindle users on 5.19. We may move this logic
     * to the native side within the native Google auth provider for 5.20+.
     * See the iOS native implementation for this in GoogleAuthProvider.m.
     */
    if (
      Platform.OS === 'android' &&
      selectedProvider.service_name === 'google' &&
      selectedProvider.auth_handler === 'browser'
    ) {
      const rootAppKey = await getRootAppKey();

      const params = [];

      if (rootAppKey) params.push(`root_app_key=${rootAppKey}`);

      if (source) params.push(`source=${source}`);

      const url = appendUrlParams(selectedProvider.auth_url, params);

      dispatchAction({
        handler: 'externalBrowser',
        url: url,
      });

      return;
    }

    ActionHandler.dispatch({
      handler: 'appUser',
      command: 'authenticate',
      authProviderId: targetProvider.authproviderid,

      /**
       * Controls which buttons will be visible in the (native) auth screen. If only one item
       * is passed, no (native) auth screen will be shown and the auth flow with the selected
       * provider will immediately be triggered.
       */
      filteredLoginAuthProviderIds: [selectedProvider.authproviderid],

      source: source,
    });
  }

  public static clearTokens(): void {
    this.clearRefreshTokenEndpoint();
    this.clearAccessToken();
    this.clearRefreshToken();
    this.notifyAuthorizationChanged();
  }

  private static clearRefreshTokenEndpoint() {
    AsyncStorage.removeItem('refresh_token_endpoint');

    if (this.refreshTokenEndpoint) {
      this.refreshTokenEndpoint = null;
    }
  }

  public static clearAccessToken(): void {
    this._accessToken = null;
    AsyncStorage.removeItem('access_token');
  }

  public static clearRefreshToken(): void {
    AsyncStorage.removeItem('refresh_token');

    if (this.refreshToken) {
      this.refreshToken = null;
    }
  }

  public static async logout(): Promise<void> {
    /**
     * Pass through the native sap-user-install-id request header
     * which is important for unlinking an install from an end user on logout in EUA
     * so that the install will not receive targeted push notifications when logged-out
     * ref: https://api.docs.subsplash.net/end-user-auth/#tag/AppInstall
     */
    let appHeaders: Record<string, string> = {};
    try {
      appHeaders = await GetAppHeaders();
    } catch {}

    try {
      debug(`Request started: ${Environment.euaService}/logout`);
      const response = await fetch(`${Environment.euaService}/logout`, {
        method: 'POST',
        // credentials: 'include' is required in order to clear the sap_token cookie on web.
        ...(Platform.OS === 'web' && { credentials: 'include' }),
        headers: {
          ...appHeaders,
          /**
           * Include end user token so that EUA knows what user
           * needs to be updated to unlink the app install on logout
           * so that the install will not continue to receive targeted notifications
           * when a user is logged out
           * ref: https://api.docs.subsplash.net/end-user-auth/#tag/AppInstall
           */
          ...(this.accessToken && {
            Authorization: this.accessToken,
          }),
        },
      });

      if (!response.ok) {
        debug(`EUA logout request failed: ${JSON.stringify(response)}`);
      }
    } catch (e) {
      debug(`EUA logout request error: ${JSON.stringify(e)}`);
    }

    debug('Clearing tokens');
    AuthService.clearTokens();
  }

  /**
   *
   * @param tokenUrl When defined, this will be used instead of the static refreshTokenUrl for token refresh.
   * @returns
   */
  public static async refreshAccessToken(
    source?: string
  ): Promise<string | null> {
    if (!this.refreshTokenEndpoint) {
      debug('skipping token refresh, no endpoint');

      return null;
    }

    const response = await this.requestAccessToken(
      this.refreshTokenEndpoint,
      source
    );

    if (!response.ok) {
      /**
       * If token refresh failed, user should be prompted to login again.
       * To prompt user to login again, we must clear both the
       * AuthService.accessToken and the redux accessToken.
       *
       * ARTEMIS-2481: This prevents a user from getting stuck
       * in an unauthorized state when switching between orgs in web messaging.
       */
      if (response.status === 401 || response.status === 403) {
        this.clearTokens();
      }

      return null;
    }

    const json = await response.json();
    this.accessToken = `${json.token_type} ${json.access_token}`;
    this.refreshToken = json.sap_token;

    this.sendRefreshTokenToNative();

    return json.access_token;
  }

  /**
   * Dev note: do not trigger any side effects such as logout on a 401 error
   * since this method maybe used outside the context of the login workflow
   * and use-case may not need any side effect,
   * e.g. FeedsService::GetCustomProfileHeader
   * Any side effects must be handled by the caller of this method.
   *
   * @param tokenUrl
   * @param source
   * @returns
   */
  public static async requestAccessToken(
    tokenUrl: string,
    source?: string
  ): Promise<Response> {
    const params = [];

    if (Platform.OS === 'web') {
      params.push('use_refresh_cookie=true');
    }

    /**
     * When a user switches apps in TCA and the user does not yet have a profile in that app, a profile will be created.
     * In this case, we need to track creation source
     * ref: https://subsplash.atlassian.net/browse/ARTEMIS-2465
     */
    if (source) {
      params.push(`source=${source}`);
    }

    const refreshUrl = appendUrlParams(tokenUrl, params);

    debug(`refreshing token with token url: ${refreshUrl}`);

    /**
     * App request headers are required for token refresh in container apps.
     * At a minimum, container apps must provide a sap-root-appkey header here.
     */
    let appHeaders: Record<string, string> = {};
    try {
      appHeaders = await GetAppHeaders();
    } catch {}

    return await fetch(refreshUrl, {
      method: 'POST',
      ...(Platform.OS === 'web'
        ? { credentials: 'include' }
        : { body: `sap_token=${this._refreshToken}` }),
      headers: {
        ...appHeaders,
        'Content-Type': 'application/x-www-form-urlencoded',
      },
    });
  }

  private static listeners: {
    [event: string]: Array<ListenerObject>;
  } = {};

  public static addListener(
    event: string,
    fn: (data: { token: string | null }) => void,
    context?: string
  ): () => void {
    if (!context) {
      context = 'unknown';
    }

    const listenerObject = { context: context, callback: fn };

    this.listeners[event]
      ? this.listeners[event].push(listenerObject)
      : (this.listeners[event] = [listenerObject]);

    return () => {
      const listenerIdx = this.listeners[event].findIndex(
        (registeredObj) => registeredObj.callback === fn
      );

      if (listenerIdx >= 0) {
        this.listeners[event].splice(listenerIdx, 1 /* delete count */);
      }
    };
  }

  private static _dispatch(
    event: string,
    data: { token: string | null }
  ): void {
    if (!this.listeners[event]) {
      return;
    }

    for (const listenerObj of this.listeners[event]) {
      listenerObj.callback(data);
    }
  }
}
